import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, ArrowUpRight, X } from "lucide-react";
import { monetize, PRODUCT_LABELS, PRODUCT_ICONS, formatPrice } from "@/lib/monetize";
import { extractFormError } from "@/lib/api";

/**
 * Public link-in-bio storefront (Pillar.io-style).
 * Fully public - no auth required. Renders a creator's storefront + product
 * grid + an inline checkout modal that captures customer email and creates
 * an order via /monetize/public/{slug}/checkout.
 */
export default function PublicStorefront() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null); // product being checked out

  useEffect(() => {
    monetize.publicStorefront(slug)
      .then((d) => setData(d))
      .catch(() => setError("This storefront isn't available."));
  }, [slug]);

  useEffect(() => {
    // Set document title/meta for SEO on public storefront pages
    if (data?.storefront) {
      document.title = `${data.storefront.title} · GoldKey Studio`;
    }
  }, [data]);

  if (error) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center text-white/60 text-sm px-6 text-center">
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center text-white/40 text-xs uppercase tracking-[0.22em]">
        Loading...
      </div>
    );
  }

  const { storefront: sf, products } = data;

  return (
    <div
      data-testid="public-storefront"
      className="min-h-screen text-white"
      style={{
        background: sf.theme === "midnight"
          ? "linear-gradient(180deg, #050b1e 0%, #0a0a0a 60%, #050505 100%)"
          : sf.theme === "editorial"
          ? "linear-gradient(180deg, #0a0a0a 0%, #141414 100%)"
          : "linear-gradient(180deg, #0a0a0a 0%, #0d0a02 60%, #050505 100%)",
      }}
    >
      {/* subtle top glow */}
      <div className="fixed top-0 inset-x-0 h-96 pointer-events-none opacity-25"
           style={{ background: `radial-gradient(circle at 50% 0%, ${sf.palette?.primary || "#d4af37"}, transparent 60%)` }} />

      <main className="relative max-w-xl mx-auto px-5 sm:px-6 py-14 sm:py-20">
        <header className="text-center">
          <div
            className="mx-auto w-24 h-24 rounded-full border-2 flex items-center justify-center overflow-hidden"
            style={{ borderColor: sf.palette?.primary || "#d4af37" }}
          >
            {sf.avatar_url ? (
              <img src={sf.avatar_url} alt={sf.title} className="w-full h-full object-cover" />
            ) : (
              <span className="font-serif text-3xl italic" style={{ color: sf.palette?.primary || "#d4af37" }}>
                {(sf.title || "GK").slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          <h1 data-testid="public-title" className="font-serif text-3xl sm:text-4xl mt-6 leading-tight">{sf.title}</h1>
          {sf.tagline && <p className="text-white/70 mt-2 leading-relaxed">{sf.tagline}</p>}
          {sf.bio && <p className="text-white/55 text-sm mt-4 leading-relaxed max-w-md mx-auto">{sf.bio}</p>}
        </header>

        <section data-testid="public-products" className="mt-10 sm:mt-14 space-y-3.5">
          {products.length === 0 && (
            <p className="text-center text-white/40 text-sm">This creator hasn&apos;t added products yet.</p>
          )}
          {products.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelected(p)}
              data-testid={`public-product-${p.id}`}
              className="w-full text-left border border-white/10 hover:border-gold/50 bg-white/[0.03] hover:bg-white/[0.06] p-5 flex items-center gap-4 transition group"
              style={{ borderRadius: sf.theme === "editorial" ? "0" : "14px" }}
            >
              <div className="text-3xl flex-none">{p.cover_emoji || PRODUCT_ICONS[p.kind]}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-gold">{PRODUCT_LABELS[p.kind]}</span>
                  <span className="text-[10px] text-white/40">·</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">{formatPrice(p.price_cents, p.currency, p.interval)}</span>
                </div>
                <p className="font-serif text-lg mt-1 leading-tight">{p.title}</p>
                {p.tagline && <p className="text-sm text-white/60 mt-1 leading-snug">{p.tagline}</p>}
              </div>
              <ArrowUpRight size={16} className="flex-none text-white/40 group-hover:text-gold transition" />
            </button>
          ))}
        </section>

        <footer className="text-center mt-16 pt-8 border-t border-white/5">
          <a
            href="https://goldkey.studio"
            className="text-[10px] uppercase tracking-[0.22em] text-white/35 hover:text-white/60"
          >
            Powered by <span className="text-gold">GoldKey</span> Studio
          </a>
        </footer>
      </main>

      {selected && <CheckoutModal product={selected} slug={slug} onClose={() => setSelected(null)} />}
    </div>
  );
}

function CheckoutModal({ product, slug, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const isFree = (product.price_cents || 0) === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const r = await monetize.publicCheckout(slug, {
        product_id: product.id,
        customer_email: email.trim(),
        customer_name: name.trim(),
      });
      setResult(r);
    } catch (err) {
      toast.error(extractFormError(err, "Couldn't process. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="public-checkout-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-5 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-ink border border-white/15 p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="public-checkout-close"
          aria-label="Close"
          className="absolute top-4 right-4 text-white/50 hover:text-white"
        >
          <X size={18} />
        </button>

        <div className="flex items-start gap-4">
          <div className="text-4xl">{product.cover_emoji || PRODUCT_ICONS[product.kind]}</div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{PRODUCT_LABELS[product.kind]}</p>
            <h3 className="font-serif text-2xl mt-1 leading-tight">{product.title}</h3>
            <p className="text-lg text-aurora-static font-medium mt-2">
              {formatPrice(product.price_cents, product.currency, product.interval)}
            </p>
          </div>
        </div>

        {product.description && <p className="text-sm text-white/70 mt-5 leading-relaxed">{product.description}</p>}

        {!result && (
          <form onSubmit={submit} className="mt-6 space-y-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name (optional)"
              maxLength={80}
              data-testid="checkout-name"
              className="w-full bg-white/[0.02] border border-white/15 focus:border-gold focus:outline-none px-4 py-3 text-sm"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email *"
              required
              maxLength={120}
              data-testid="checkout-email"
              className="w-full bg-white/[0.02] border border-white/15 focus:border-gold focus:outline-none px-4 py-3 text-sm"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="checkout-submit"
              className="btn-aurora w-full justify-center disabled:opacity-60"
            >
              {busy ? <><Loader2 size={13} className="animate-spin" /> Processing...</> :
                isFree ? "Get it now" : "Complete order"}
            </button>
            {!isFree && (
              <p className="text-[10px] text-white/40 text-center leading-relaxed">
                Your order is recorded and the creator will follow up via email to complete payment and delivery.
              </p>
            )}
          </form>
        )}

        {result && (
          <div data-testid="checkout-success" className="mt-6">
            <div className="w-14 h-14 mx-auto rounded-full border-2 border-gold flex items-center justify-center">
              <Check size={22} className="text-gold" />
            </div>
            <p className="font-serif text-2xl text-center mt-4">Thank you!</p>
            <p className="text-sm text-white/70 text-center mt-3 leading-relaxed">
              {result.status === "paid" && result.delivery_url ? (
                <>Your access is ready. <a href={result.delivery_url} className="text-gold underline-offset-4 hover:underline" target="_blank" rel="noreferrer">Open it here.</a></>
              ) : result.status === "paid" ? (
                <>Your access has been unlocked. Check your email for the details.</>
              ) : (
                <>{result.message || "The creator will be in touch shortly."}</>
              )}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost mt-6 w-full justify-center"
              data-testid="checkout-close-done"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
